import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Button } from "../ui/Button";

/**
 * Warning acknowledgements (U-15, brief 5.2).
 *
 * A warned gallery is acknowledged only when the user presses THAT gallery's confirm button
 * (`ConfirmWarningButton`, the single caller of `confirm`). The set starts empty, lives in memory only,
 * holds single gallery ids and is never filled in bulk, so nothing is acknowledged by default and a reload
 * forgets everything. `ackInput` is the only place the `ack` flag of a request is made.
 */
interface AckApi {
  isConfirmed(gid: number | string): boolean;
  confirm(gid: number | string): void;
  /** the server said the gallery needs a confirmation the client believed it had: ask again */
  revoke(gid: number | string): void;
}

const Ctx = createContext<AckApi | null>(null);

export function AckProvider({ children }: { children: ReactNode }) {
  const [confirmed, setConfirmed] = useState<ReadonlySet<number | string>>(() => new Set());

  const confirm = useCallback((gid: number | string) => setConfirmed((prev) => new Set(prev).add(gid)), []);
  const revoke = useCallback(
    (gid: number | string) =>
      setConfirmed((prev) => {
        const next = new Set(prev);
        next.delete(gid);
        return next;
      }),
    [],
  );
  const api = useMemo<AckApi>(
    () => ({ isConfirmed: (gid) => confirmed.has(gid), confirm, revoke }),
    [confirmed, confirm, revoke],
  );
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useAcks(): AckApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAcks outside AckProvider");
  return v;
}

/** The `ack` part of a download or reader request: present only after the user confirmed this gallery. */
export function ackInput(acks: AckApi, gid: number | string): { ack?: true } {
  return acks.isConfirmed(gid) ? { ack: true } : {};
}

/** The confirm button of one warned gallery. Pressing it is the only way a gallery becomes acknowledged. */
export function ConfirmWarningButton({ gid, primary = false }: { gid: number | string; primary?: boolean }) {
  const acks = useAcks();
  return (
    <Button
      variant={primary ? "primary" : "plain"}
      className="confirm-warning"
      onClick={() => acks.confirm(gid)}
    >
      확인하고 보기
    </Button>
  );
}
